import type { CasaDeTienda, FilaVista } from '../types/metas.types';

/**
 * EL RENGLÓN «CASA <tienda>» (23-oct-2026, ronda F, decisión del usuario: «Sí,
 * opcional»). «Casa X» son los agentes de Proscai llamados «CASA …» con los que
 * el gerente cobra cuando el único vendedor descansa: la venta va a la tienda y
 * no se le paga a nadie. El supervisor agrega el renglón cuando hace falta
 * («+ Casa»); su meta cuenta para cuadrar la meta de la tienda, nadie cobra
 * comisión por ella (ni el gerente) y sale con aviso, como la del bodeguero.
 *
 * La misma regla que `domain/cuenta-de-tienda.ts` de la API: la llave es
 * `casa:<CADENA>:<num>` (la cadena en mayúsculas), su regla es `opcional`
 * (nunca queda pendiente) y SÓLO lleva meta: sin motivo, apoyo, transporte,
 * bodeguero fijo ni días de incapacidad (`CASA_SOLO_META`).
 */

/** El puesto con que la API manda la fila. */
export const PUESTO_CASA = 'Casa';

/** `casa:VAZ:57`: no choca con `buk:<n>` ni con `CADER:`/`TRENDER:`. */
export const esLlaveCasa = (k: string | null | undefined): boolean => /^casa:[A-Z]{3}:\d+$/.test(String(k ?? ''));

/** La fila es la Casa: la API la marca (`esCasa`) y, por si acaso, su llave lo dice. */
export const esFilaCasa = (f: { key?: string | null; esCasa?: boolean }): boolean => f.esCasa === true || esLlaveCasa(f.key);

/** ¿«Se le está colocando meta a la Casa»? Con meta en pesos (mayor que cero). */
export const esMetaACasa = (f: { key?: string | null; esCasa?: boolean; metaMonto: number | null | undefined }): boolean =>
  esFilaCasa(f) && f.metaMonto !== null && f.metaMonto !== undefined && Number.isFinite(f.metaMonto) && f.metaMonto > 0;

/** El código de la advertencia de la API: la pantalla la pinta en vivo, no la repite. */
export const CODIGO_META_A_CASA = 'META_A_CASA';

/** El aviso, como el del bodeguero. */
export const TEXTO_META_A_CASA = 'Se le está colocando meta a la Casa';

/** Bajo el nombre de la Casa, en la captura. */
export const DESCRIPCION_CASA =
  'Casa de la tienda · no es una persona: su meta cuenta para la tienda y nadie la cobra, ni el gerente';

/** El `title` de «+ Casa». */
export const TITULO_AGREGAR_CASA =
  'Agrega el renglón de la Casa: para la venta que se cobra con la cuenta «CASA…» cuando el único vendedor descansa. Su meta cuenta para la meta de la tienda; nadie la cobra';

/** Lo que explica la casilla de la meta de la Casa (`title`). */
export const TITULO_META_CASA =
  'Opcional: la meta de la Casa cuenta para cuadrar la meta de la tienda, pero nadie la cobra (ni el gerente). No se copia a la semana siguiente.';

/**
 * La fila que pone «+ Casa» en la copia local, con lo que manda la vista
 * (`vista.casa`). Se guarda con el `PUT` de siempre y `agregar: [llave]`; la
 * respuesta trae la de la API, que la reemplaza. Sin promedio, sin agente y sin
 * número de BUK: no es una persona.
 */
export function filaCasaNueva(casa: Pick<CasaDeTienda, 'key' | 'nombre'>): FilaVista {
  return {
    key: casa.key,
    numero: null,
    base: null,
    agseq: null,
    agnum: null,
    nombre: casa.nombre,
    puesto: PUESTO_CASA,
    puestoFuente: null,
    fechaAlta: null,
    estadoBuk: null,
    antiguedad: { semana: null, etapa: null },
    origen: 'manual',
    enPlantilla: true,
    otraTienda: null,
    ventaRef4s: null,
    ventaRefSemanal: null,
    ventaPromedio: null,
    ventaPromedioSemanas: null,
    vsPromedio: null,
    reglaMeta: 'opcional',
    esquema: null,
    metaSemanaAnterior: null,
    metaMonto: null,
    sinMetaMotivo: null,
    sinMetaDetalle: null,
    apoyo: null,
    suplente: null,
    esBodega: false,
    esCasa: true,
  };
}
